import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { 
  CircleNotch,
  Trash,
  ArrowCounterClockwise,
  Warning,
  FileText,
  MagnifyingGlass,
  FunnelSimple,
  ShieldWarning,
  X
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'quotations', label: 'Quotations' },
  { value: 'purchase_orders', label: 'Purchase Orders' },
  { value: 'grns', label: 'GRNs' },
  { value: 'rfqs', label: 'RFQs' },
  { value: 'payment_vouchers', label: 'Payment Vouchers' },
  { value: 'receipt_vouchers', label: 'Receipt Vouchers' }
];

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function DeletedDocumentsPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [deletedDocs, setDeletedDocs] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const fetchDeletedDocuments = async () => {
    try {
      setLoading(true);
      const params = filterType && filterType !== 'all' ? `?document_type=${filterType}` : '';
      const res = await axios.get(`${API_URL}/api/documents/deleted${params}`, { headers });
      setDeletedDocs(res.data.deleted_documents || []);
    } catch (err) {
      console.error('Failed to fetch deleted documents:', err);
      toast.error('Failed to load deleted documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchDeletedDocuments();
    }
  }, [filterType]);

  const handleRestore = async () => {
    if (!selectedDoc) return;
    try {
      await axios.post(`${API_URL}/api/documents/restore/${selectedDoc.id}`, {}, { headers });
      toast.success('Document restored successfully');
      setShowRestoreDialog(false);
      setSelectedDoc(null);
      fetchDeletedDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore document');
    }
  };

  const handlePermanentDelete = async () => {
    if (!selectedDoc) return;
    try {
      await axios.delete(`${API_URL}/api/documents/permanent/${selectedDoc.id}`, { headers });
      toast.success('Document permanently deleted');
      setShowDeleteDialog(false);
      setSelectedDoc(null);
      fetchDeletedDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete document');
    }
  };

  const filteredDocs = deletedDocs.filter(doc => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      doc.document_number?.toLowerCase().includes(term) ||
      doc.deleted_by_name?.toLowerCase().includes(term) ||
      doc.delete_reason?.toLowerCase().includes(term)
    );
  });

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <ShieldWarning className="w-16 h-16 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold">Admin Access Required</h2>
        <p className="text-muted-foreground mt-2">You need admin privileges to view deleted documents.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <CircleNotch className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="deleted-documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Trash className="w-7 h-7 text-red-400" weight="duotone" />
            Deleted Documents
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage soft-deleted documents - restore or permanently remove
          </p>
        </div>
        
        <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 px-3 py-1">
          <Warning className="w-4 h-4 mr-2" />
          {deletedDocs.length} deleted documents
        </Badge>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by document number, deleted by..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="deleted-search"
          />
        </div>
        
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[200px]" data-testid="deleted-type-filter">
            <FunnelSimple className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            {DOCUMENT_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Document List */}
      <div className="space-y-3">
        {filteredDocs.length > 0 ? (
          filteredDocs.map((doc) => (
            <motion.div
              key={doc.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-xl p-4 hover:border-red-500/30 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-red-500/20">
                    <FileText className="w-6 h-6 text-red-400" weight="bold" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{doc.document_number || 'No Number'}</h3>
                      <Badge variant="outline" className="text-xs">
                        {DOCUMENT_TYPES.find(t => t.value === doc.document_type)?.label || doc.document_type}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Deleted by <span className="text-foreground">{doc.deleted_by_name}</span> on {formatDate(doc.deleted_at)}
                    </p>
                    {doc.delete_reason && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Reason: {doc.delete_reason}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-green-500 text-green-500 hover:bg-green-500/10"
                    onClick={() => {
                      setSelectedDoc(doc);
                      setShowRestoreDialog(true);
                    }}
                    data-testid={`restore-doc-${doc.id}`}
                  >
                    <ArrowCounterClockwise className="w-4 h-4 mr-1" />
                    Restore
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-red-500 text-red-500 hover:bg-red-500/10"
                    onClick={() => {
                      setSelectedDoc(doc);
                      setShowDeleteDialog(true);
                    }}
                    data-testid={`permanent-delete-${doc.id}`}
                  >
                    <Trash className="w-4 h-4 mr-1" />
                    Delete Forever
                  </Button>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Trash className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">No Deleted Documents</h3>
              <p className="text-muted-foreground mt-2">
                {filterType && filterType !== 'all' ? 'No deleted documents of this type' : 'All documents are active'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowCounterClockwise className="w-5 h-5 text-green-500" />
              Restore Document
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to restore <strong>{selectedDoc?.document_number}</strong>?
              The document will be moved back to active documents.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestoreDialog(false)}>
              Cancel
            </Button>
            <Button className="bg-green-500 hover:bg-green-600" onClick={handleRestore}>
              <ArrowCounterClockwise className="w-4 h-4 mr-2" />
              Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Warning className="w-5 h-5" />
              Permanently Delete Document
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to <strong>permanently delete</strong> <strong>{selectedDoc?.document_number}</strong>?
              <br /><br />
              <span className="text-red-400 font-medium">
                This action cannot be undone. The document and all its attachments will be permanently removed from the system.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handlePermanentDelete}>
              <Trash className="w-4 h-4 mr-2" />
              Delete Forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
